<!--
  Ihre Leistungen (Plan „Eine Zentrale" S4, 17.10.2026): was läuft, was es kostet, kündigen,
  Zahlungsart. Gebucht wird nichts mehr selbst – neue Leistungen vereinbaren Kunden mit uns.
-->
<script lang="ts">
  import { api } from './api.ts';
  import { sitzung, euro, datum, PAKETNAME } from './sitzung.svelte.ts';
  import ZahlungsmethodeErfassen from './ZahlungsmethodeErfassen.svelte';

  interface Leistung { id: string; name: string; einheit: string; menge: number; beginn: string; ende: string | null; monat: number; zahlweg: string; gekuendigt: boolean; aktiv: boolean; wartet_auf_zahlung: boolean }

  let leistungen = $state<Leistung[]>([]);
  let hinweis = $state('');
  let fehler = $state('');
  let laeuft = $state('');
  let zahlungsart = $state(false);
  let zahlungsartVorhanden = $state<boolean | null>(null);

  async function laden() {
    try { leistungen = (await api.get<{ leistungen: Leistung[] }>('/api/portal/leistungen')).leistungen; } catch { leistungen = []; }
    try { zahlungsartVorhanden = (await api.get<{ vorhanden: boolean }>('/api/portal/zahlungsmethode')).vorhanden; } catch { zahlungsartVorhanden = null; }
  }
  $effect(() => { void laden(); });

  const altvertraege = $derived(sitzung.pakete.filter((p) => p.status === 'aktiv' || p.status === 'gekuendigt' || p.status === 'gesperrt'));
  const zahlweise = (z: string | null) => (z === 'jaehrlich' ? 'im Jahr' : z === 'quartalsweise' ? 'im Quartal' : 'im Monat');

  async function kuendigen(l: Leistung) {
    if (!confirm(`„${l.name}" zum nächstmöglichen Termin kündigen?`)) return;
    laeuft = l.id; fehler = ''; hinweis = '';
    try { hinweis = (await api.post<{ text: string }>(`/api/portal/leistungen/${l.id}/kuendigen`)).text; await laden(); }
    catch (e) { fehler = e instanceof Error ? e.message : 'Das hat gerade nicht geklappt'; }
    finally { laeuft = ''; }
  }
  async function altKuendigen(id: string) {
    if (!confirm('Diesen Vertrag zum nächstmöglichen Termin kündigen?')) return;
    laeuft = id; fehler = ''; hinweis = '';
    try { await api.patch(`/api/portal/pakete/${id}/kuendigen`, {}); hinweis = 'Die Kündigung ist vermerkt. Sie bekommen eine Bestätigung per E-Mail.'; await sitzung.laden(); }
    catch (e) { fehler = e instanceof Error ? e.message : 'Das hat gerade nicht geklappt'; }
    finally { laeuft = ''; }
  }
  async function altReaktivieren(id: string) {
    laeuft = id; fehler = ''; hinweis = '';
    try { await api.patch(`/api/portal/pakete/${id}/reaktivieren`, {}); hinweis = 'Die Kündigung ist zurückgenommen.'; await sitzung.laden(); }
    catch (e) { fehler = e instanceof Error ? e.message : 'Das hat gerade nicht geklappt'; }
    finally { laeuft = ''; }
  }
</script>

<div class="karte">
  <h2>Ihre Leistungen</h2>
  {#if hinweis}<p class="erfolg">{hinweis}</p>{/if}
  {#if fehler}<p class="fehler">{fehler}</p>{/if}
  {#if !leistungen.length && !altvertraege.length}
    <p class="leise">Zurzeit läuft keine Leistung. Wenn Sie etwas brauchen, stellen Sie einfach eine Anfrage oder rufen Sie an.</p>
  {:else}
    <ul class="eintraege">
      {#each leistungen as l (l.id)}
        <li>
          <div class="zeile">
            <strong>{l.name}</strong>
            <span class="marke" class:gut={l.aktiv && !l.gekuendigt} class:schlecht={l.wartet_auf_zahlung}>{l.wartet_auf_zahlung ? 'wartet auf Zahlung' : l.gekuendigt ? `endet ${l.ende ? datum(l.ende) : 'bald'}` : l.aktiv ? 'aktiv' : l.ende ? 'beendet' : 'ab ' + datum(l.beginn)}</span>
          </div>
          <div class="leise klein">{l.menge > 1 ? `${l.menge} × ` : ''}{l.einheit}{l.monat ? ` · ${euro(l.monat)} im Monat` : ' · kostenfrei'}{l.zahlweg === 'stripe' ? ' · wird abgebucht' : l.zahlweg === 'rechnung' ? ' · per Rechnung' : ''} · seit {datum(l.beginn)}</div>
          {#if l.aktiv && !l.gekuendigt && l.monat > 0}
            <div class="knopfreihe"><button type="button" class="sekundaer" disabled={laeuft === l.id} onclick={() => kuendigen(l)}>Kündigen</button></div>
          {/if}
        </li>
      {/each}
      {#each altvertraege as p (p.id)}
        <li>
          <div class="zeile">
            <strong>{p.name ?? PAKETNAME[p.typ] ?? p.typ}</strong>
            <span class="marke" class:gut={p.status === 'aktiv' && !p.endet_am} class:schlecht={p.status === 'gesperrt'}>{p.status === 'gesperrt' ? 'pausiert' : p.endet_am ? `endet ${datum(p.endet_am)}` : 'aktiv'}</span>
          </div>
          <div class="leise klein">{#if p.preis}{euro(p.preis)} {zahlweise(p.zahlweise)}{/if}{#if p.aktiv_seit} · seit {datum(p.aktiv_seit)}{/if}</div>
          {#if p.status === 'aktiv' && !sitzung.kunde?.mitarbeiter}
            <div class="knopfreihe">
              {#if p.endet_am}<button type="button" class="sekundaer" disabled={laeuft === p.id} onclick={() => altReaktivieren(p.id)}>Kündigung zurücknehmen</button>
              {:else}<button type="button" class="sekundaer" disabled={laeuft === p.id} onclick={() => altKuendigen(p.id)}>Kündigen</button>{/if}
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
  <p class="leise klein" style="margin-top:0.8rem">Neue Leistungen vereinbaren Sie mit uns persönlich – über eine Anfrage oder einen Anruf. Sie bekommen dann ein Angebot oder einen Zahlungslink per E-Mail.</p>
  {#if zahlungsartVorhanden !== null && !sitzung.kunde?.mitarbeiter}
    <div class="knopfreihe">
      <button type="button" class="sekundaer" onclick={() => (zahlungsart = !zahlungsart)}>{zahlungsartVorhanden ? 'Zahlungsart ändern' : 'Zahlungsart hinterlegen'}</button>
    </div>
    {#if zahlungsart}<ZahlungsmethodeErfassen onErfasst={() => { zahlungsart = false; hinweis = 'Zahlungsart gespeichert.'; void laden(); }} onAbbrechen={() => (zahlungsart = false)} />{/if}
  {/if}
</div>
